import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "input", "submitButton", "warning"]
  static values = { 
    userId: Number, 
    monthPayrollId: Number,
    readOnly: { type: Boolean, default: false }
  }

  connect() {
    console.log("Work days controller connected")
    this.initializeCalendarModal()
  }

  // Перехватываем отправку формы и делаем AJAX запрос
  submitForm(event) {
    event.preventDefault()
    console.log("Form submit intercepted")
    
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = "Сохранение..."
    }
    
    const formData = new FormData(this.formTarget)
    const csrfToken = document.querySelector('[name="csrf-token"]').content
    
    fetch(this.formTarget.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    })
    .then(data => {
      this.processSuccessResponse(data)
    })
    .catch(error => {
      console.error("Form submission error:", error)
      this.showNotification("Произошла ошибка при обновлении", "error")
      this.resetSubmitButton()
    })
  }

  processSuccessResponse(data) {
    console.log("Success response:", data)
    
    if (data.success) {
      // Показываем уведомление об успехе
      this.showNotification(data.message || "Количество запланированных дней обновлено", "success")
      
      // Заменяем HTML содержимое элемента, сохраняя Stimulus контроллер
      if (data.html) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.html
        const newContent = tempDiv.firstElementChild
        
        // Копируем содержимое из нового элемента
        if (newContent) {
          this.element.innerHTML = newContent.innerHTML
          // Обновляем data-атрибуты контроллера если они изменились
          if (newContent.dataset.workDaysUserIdValue) {
            this.element.dataset.workDaysUserIdValue = newContent.dataset.workDaysUserIdValue
          }
          if (newContent.dataset.workDaysMonthPayrollIdValue) {
            this.element.dataset.workDaysMonthPayrollIdValue = newContent.dataset.workDaysMonthPayrollIdValue
          }
        }
      }
      
      // Обновляем расчеты зарплаты если есть контроллер salary-calculation
      this.updateSalaryCalculation(data)
    } else {
      this.showNotification(data.error || "Произошла ошибка", "error")
      this.resetSubmitButton()
    }
  }

  updateSalaryCalculation(data) {
    // Сначала пробуем найти новый salary-state контроллер
    const salaryStateElement = document.querySelector('[data-controller*="salary-state"]')
    
    if (salaryStateElement) {
      const salaryStateController = this.application.getControllerForElementAndIdentifier(
        salaryStateElement, 
        "salary-state"
      )
      
      if (salaryStateController) {
        // Обновляем все data-атрибуты контроллера с новыми значениями с сервера
        if (data.worked_days !== undefined) {
          salaryStateElement.dataset.salaryStateWorkedDaysValue = data.worked_days
        }
        if (data.planned_days !== undefined) {
          salaryStateElement.dataset.salaryStatePlannedDaysValue = data.planned_days
        }
        if (data.total_personal_profit !== undefined) {
          salaryStateElement.dataset.salaryStatePersonalProfitValue = data.total_personal_profit
        }
        if (data.total_progressive_profit !== undefined) {
          salaryStateElement.dataset.salaryStateProgressiveProfitValue = data.total_progressive_profit
        }
        if (data.subordinates_profit !== undefined) {
          salaryStateElement.dataset.salaryStateSubordinatesProfitValue = data.subordinates_profit
        }
        if (data.total_month_profit !== undefined) {
          salaryStateElement.dataset.salaryStateTotalMonthProfitValue = data.total_month_profit
        }
        
        // Заставляем контроллер переинициализировать состояние с новыми данными
        salaryStateController.updateStateFromServerData(data)
        return
      }
    }
    
    // Fallback: ищем старый salary-calculation контроллер для обратной совместимости
    const salaryElement = document.querySelector('[data-controller*="salary-calculation"]')
    
    if (salaryElement) {
      const salaryCalculationController = this.application.getControllerForElementAndIdentifier(
        salaryElement, 
        "salary-calculation"
      )
      
      if (salaryCalculationController) {
        // Обновляем фиксированную зарплату если она изменилась
        if (data.fixed_salary_amount !== undefined) {
          salaryCalculationController.updateFixedSalary(data.fixed_salary_amount)
        }
        
        // Пересчитываем итоговую сумму
        if (salaryCalculationController.recalculateTotal) {
          salaryCalculationController.recalculateTotal()
        }
      }
    }
  }

  // Вспомогательный метод для показа уведомлений
  showNotification(message, type = "info") {
    // Создаем временное уведомление Bootstrap
    const alertClass = type === "success" ? "alert-success" : "alert-danger"
    const alert = document.createElement("div")
    alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`
    alert.style.cssText = "top: 20px; right: 20px; z-index: 9999; min-width: 300px;"
    alert.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `
    
    document.body.appendChild(alert)
    
    // Автоматически удаляем через 5 секунд
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove()
      }
    }, 5000)
  }

  resetSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Сохранить"
    }
  }

  showCalendar() {
    console.log("Show calendar clicked")
    this.loadCalendarData()
  }

  loadCalendarData() {
    const url = `/month_payrolls/${this.monthPayrollIdValue}/work_calendar?user_id=${this.userIdValue}`
    const csrfToken = document.querySelector('[name="csrf-token"]').content
    
    fetch(url, {
      method: 'GET',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    })
    .then(data => {
      if (data.success) {
        this.renderCalendar(data.calendar_data)
        this.showCalendarModal()
      } else {
        this.showNotification("Ошибка при загрузке календаря", "error")
      }
    })
    .catch(error => {
      console.error("Calendar loading error:", error)
      this.showNotification("Ошибка при загрузке календаря", "error")
    })
  }

  renderCalendar(calendarData) {
    const calendar = this.buildCalendarHTML(calendarData)
    const calendarContainer = this.getCalendarContainer()
    
    if (calendarContainer) {
      this.disposeTooltipsWithin(calendarContainer)
      calendarContainer.innerHTML = calendar

      if (this.readOnlyValue) {
        return
      }

      // Добавляем обработчики событий для кнопок
      calendarContainer.querySelectorAll('button[data-date]').forEach(btn => {
        btn.addEventListener('click', (event) => {
          const date = event.currentTarget.dataset.date
          const status = event.currentTarget.dataset.status
          this.toggleWorkDate(date, status, event.currentTarget)
        })
      })
    }
  }

  buildCalendarHTML(calendarData) {
    const buttonType = 'button'
    const readOnlyAttrs = this.readOnlyValue ? 'disabled aria-disabled="true" tabindex="-1"' : ''

    let html = `
      <div class="calendar-header mb-3">
        <h5 class="text-center mb-3">${calendarData.month_name}</h5>
        <div class="row text-center small fw-bold">
    `
    
    calendarData.days_of_week.forEach(day => {
      html += `<div class="col">${day}</div>`
    })
    
    html += `
        </div>
      </div>
      <div class="calendar-body">
    `
    
    calendarData.weeks.forEach(week => {
      html += '<div class="row mb-1">'
      week.forEach(day => {
        const isCurrentMonth = day.is_current_month
        const status = day.status
        const hasComment = isCurrentMonth && day.has_comment
        const encodedComment = hasComment ? this.encodeDataAttribute(day.comment) : ''
        const commentIndicator = hasComment ? `
          <span class="position-absolute top-0 end-0 mt-1 me-1 text-secondary"
                tabindex="0"
                role="button"
                aria-label="Комментарий к дню"
                title="${this.escapeAttribute(day.comment)}"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-html="true"
                data-bs-custom-class="month-plan-cell__tooltip"
                data-work-days-comment="${encodedComment}">
            <i class="fas fa-comment-dots"></i>
          </span>
        ` : ''
        const statusButtons = isCurrentMonth ? `
          <button type="${buttonType}" class="btn btn-sm flex-grow-1 p-0 ${status === 'working' ? 'btn-primary' : 'btn-outline-primary'}" 
                        style="min-width: 24px; font-size: 10px; height: 20px;"
                        data-date="${day.date_string}" 
            data-status="working" title="Работал" ${readOnlyAttrs}>Р</button>
          <button type="${buttonType}" class="btn btn-sm flex-grow-1 p-0 ${status === 'sick' ? 'btn-danger' : 'btn-outline-danger'}" 
                        style="min-width: 18px; font-size: 10px; height: 20px;"
                        data-date="${day.date_string}" 
            data-status="sick" title="Больничный" ${readOnlyAttrs}>Б</button>
          <button type="${buttonType}" class="btn btn-sm flex-grow-1 p-0 ${status === 'vacation' ? 'btn-success' : 'btn-outline-success'}" 
                        style="min-width: 18px; font-size: 10px; height: 20px;"
                        data-date="${day.date_string}" 
            data-status="vacation" title="Отпуск" ${readOnlyAttrs}>О</button>
        ` : '<div style="height: 20px;"></div>'
        
        html += `
          <div class="col p-1">
            <div class="calendar-day-container border rounded p-1 position-relative ${isCurrentMonth ? 'bg-white' : 'bg-light text-muted'}" 
                 style="${isCurrentMonth ? '' : 'pointer-events: none;'} overflow: visible;">
              ${commentIndicator}
              <div class="text-center small mb-1 fw-bold">${day.day}</div>
              <div class="d-flex gap-1">
                ${statusButtons}
              </div>
            </div>
          </div>
        `
      })
      html += '</div>'
    })
    
    html += '</div>'
    return html
  }

  encodeDataAttribute(value) {
    return encodeURIComponent(value || '')
  }

  decodeDataAttribute(value) {
    try {
      return decodeURIComponent(value || '')
    } catch (_error) {
      return value || ''
    }
  }

  formatTooltipHtml(value) {
    return this.escapeHtml(value)
      .replace(/\r\n|\r|\n/g, '<br>')
  }

  escapeHtml(value) {
    const element = document.createElement('div')
    element.textContent = value || ''
    return element.innerHTML
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;')
  }

  toggleWorkDate(date, status, btnElement) {
    const url = `/month_payrolls/${this.monthPayrollIdValue}/toggle_work_date?user_id=${this.userIdValue}`
    const csrfToken = document.querySelector('[name="csrf-token"]').content
    
    // Визуально показываем, что идет обработка
    const container = btnElement.closest('.calendar-day-container')
    if (container) container.style.opacity = '0.5'
    
    const formData = new FormData()
    formData.append('date', date)
    formData.append('status', status)
    
    fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    })
    .then(data => {
      if (data.success) {
        // Обновляем внешний вид кнопок в этом дне
        if (container) {
          this.updateDayButtons(container, data.status)
        }
        
        // Заменяем HTML содержимое элемента, сохраняя Stimulus контроллер
        if (data.html) {
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = data.html
          const newContent = tempDiv.firstElementChild
          
          if (newContent) {
            this.element.innerHTML = newContent.innerHTML
          }
        }
        
        // Обновляем расчеты зарплаты
        this.updateSalaryCalculation(data)
        
        this.showNotification(data.message, "success")
      } else {
        this.showNotification(data.error || "Ошибка при обновлении", "error")
      }
    })
    .catch(error => {
      console.error("Toggle work date error:", error)
      this.showNotification("Ошибка при обновлении дня", "error")
    })
    .finally(() => {
      if (container) container.style.opacity = '1'
    })
  }

  updateDayButtons(container, status) {
    const btnWorking = container.querySelector('button[data-status="working"]')
    const btnSick = container.querySelector('button[data-status="sick"]')
    const btnVacation = container.querySelector('button[data-status="vacation"]')

    // Сбрасываем все кнопки
    btnWorking.className = 'btn btn-sm flex-grow-1 p-0 btn-outline-primary'
    btnSick.className = 'btn btn-sm p-0 btn-outline-danger'
    btnVacation.className = 'btn btn-sm p-0 btn-outline-success'

    // Устанавливаем активную
    if (status === 'working') {
      btnWorking.className = 'btn btn-sm flex-grow-1 p-0 btn-primary'
    } else if (status === 'sick') {
      btnSick.className = 'btn btn-sm p-0 btn-danger'
    } else if (status === 'vacation') {
      btnVacation.className = 'btn btn-sm p-0 btn-success'
    }
  }

  initializeTooltipsWithin(rootElement) {
    if (!window.bootstrap?.Tooltip) return

    rootElement.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
      const instance = window.bootstrap.Tooltip.getInstance(element)
      if (instance) {
        instance.dispose()
      }

      const comment = this.decodeDataAttribute(element.dataset.workDaysComment)
      element.setAttribute('data-bs-title', this.formatTooltipHtml(comment))
      element.removeAttribute('title')

      window.bootstrap.Tooltip.getOrCreateInstance(element, {
        container: "body",
        boundary: document.body
      })
    })
  }

  disposeTooltipsWithin(rootElement) {
    if (!window.bootstrap?.Tooltip) return

    rootElement.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
      const instance = window.bootstrap.Tooltip.getInstance(element)
      if (!instance) return

      instance.hide()
      instance.dispose()
    })
  }

  initializeCalendarModal() {
    const modalTitle = 'Календарь дней'
    const modalDescription = this.readOnlyValue ? 'Только просмотр статусов по дням месяца' : 'Выберите статус каждого дня'

    // Создаем модальное окно если его нет в документе
    let modal = document.getElementById('workCalendarModal')
    if (!modal) {
      const modalWrapper = document.createElement('div')
      modalWrapper.innerHTML = `
        <div class="modal fade" id="workCalendarModal" tabindex="-1">
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">${modalTitle}</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <div class="mb-3">
                  <div class="small text-muted mb-2" data-work-days-modal-description>${modalDescription}</div>
                  <small class="text-muted">
                    <span class="badge bg-primary me-2">Р</span> Работал
                    <span class="badge bg-danger ms-3 me-2">Б</span> Больничный
                    <span class="badge bg-success ms-3 me-2">О</span> Отпуск
                  </small>
                </div>
                <div id="workCalendarContainer"></div>
              </div>
            </div>
          </div>
        </div>
      `
      document.body.appendChild(modalWrapper.firstElementChild)
      modal = document.getElementById('workCalendarModal')
    }

    if (modal) {
      const titleElement = modal.querySelector('.modal-title')
      const descriptionElement = modal.querySelector('[data-work-days-modal-description]')

      if (titleElement) {
        titleElement.textContent = modalTitle
      }

      if (descriptionElement) {
        descriptionElement.textContent = modalDescription
      }
    }
  }

  getCalendarContainer() {
    return document.getElementById('workCalendarContainer')
  }

  showCalendarModal() {
    const modal = document.getElementById('workCalendarModal')
    if (modal) {
      modal.addEventListener('shown.bs.modal', () => {
        requestAnimationFrame(() => {
          this.initializeCalendarTooltips()
        })
      }, { once: true })

      const bootstrapModal = new bootstrap.Modal(modal)
      bootstrapModal.show()
    }
  }

  initializeCalendarTooltips() {
    const calendarContainer = this.getCalendarContainer()
    if (!calendarContainer) return

    this.disposeTooltipsWithin(calendarContainer)
    this.initializeTooltipsWithin(calendarContainer)
  }
}
